/**
 * @features/dashboard/components/mobile/MobileQuickActions.jsx
 * -----------------------------------------------------------
 * Horizontal-scroll pill row of role-specific primary actions. Lives
 * directly under the mobile dashboard header (hidden on desktop) and
 * gives one-tap access to high-frequency CTAs without crowding the
 * 5-slot bottom-nav.
 *
 * Each action is either a route link or an `onClick` callback:
 *   { id, label, icon, to?, onClick?, primary?, testid? }
 *
 * Visual treatment:
 *   • Default chip: white pill, brand-blue text, neutral border.
 *   • `primary`:    solid brand-blue fill, white text.
 *   • Horizontal scroll allowed (overflow-x-auto + snap-x).
 *
 * v2.71.38 — Initial.
 */

import React from "react";
import { Link } from "react-router-dom";

export function MobileQuickActions({ actions }) {
  const list = Array.isArray(actions) ? actions.filter(Boolean) : [];
  if (list.length === 0) return null;

  return (
    <div
      className="md:hidden -mx-4 px-4 mb-3"
      data-testid="mobile-quick-actions"
    >
      <div className="flex items-center gap-2 overflow-x-auto allow-x-scroll snap-x snap-mandatory pb-1 -mb-1">
        {list.map((a) => {
          const Icon = a.icon;
          const cls = [
            "snap-start flex-shrink-0 inline-flex items-center gap-1.5",
            "h-9 px-3 rounded-sm text-[12px] font-semibold tracking-tight",
            "border transition-colors",
            a.primary
              ? "bg-[#0055FF] text-white border-[#0055FF] hover:bg-[#0044CC]"
              : "bg-white text-[#27272A] border-[#E4E4E7] hover:border-[#27272A]",
          ].join(" ");
          if (a.to) {
            return (
              <Link
                key={a.id}
                to={a.to}
                className={cls}
                data-testid={a.testid || `mqa-${a.id}`}
              >
                {Icon ? <Icon size={13} strokeWidth={2} /> : null}
                <span className="whitespace-nowrap">{a.label}</span>
              </Link>
            );
          }
          return (
            <button
              key={a.id}
              type="button"
              onClick={a.onClick}
              className={cls}
              data-testid={a.testid || `mqa-${a.id}`}
            >
              {Icon ? <Icon size={13} strokeWidth={2} /> : null}
              <span className="whitespace-nowrap">{a.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default MobileQuickActions;
